
import { db } from './firebase';
import { doc, getDoc, setDoc, deleteDoc, deleteField, collection, getDocs, writeBatch, query, where } from 'firebase/firestore';
import type { IconType, IconCategory, CustomBackground, BackgroundCategory, CustomShape, CustomTemplate, VariantSettings } from '../types';

const SETTINGS_COLLECTION = 'settings';

const COL_ICONS = 'custom_icons';
const COL_BGS = 'custom_backgrounds';
const COL_SHAPES = 'custom_shapes';
const COL_TEMPLATES = 'custom_templates';

/**
 * Returns the Firestore document ID for this deployment instance.
 * Set via window.__INSTANCE_ID__ injected by server.js from the INSTANCE_ID env var.
 * Falls back to 'general' for local dev and legacy deployments.
 */
export function getInstanceId(): string {
    if (typeof window !== 'undefined' && (window as any).__INSTANCE_ID__) {
        return (window as any).__INSTANCE_ID__;
    }
    return 'general';
}

export interface UserPermissions {
  allowAiGenerator: boolean;
  allowIconUpload: boolean;
  allowGlobalColors: boolean;
  allowFontSelection: boolean;
  allowStickerDimensions: boolean;
  allowStickerCount: boolean;
  allowTextStyling: boolean;
  allowFineTuning: boolean;
  allowFullImageUpload: boolean;
  allowSheetPositioning: boolean;
  allowStickerPositioning: boolean;
  allowNameField: boolean;
  allowClassNameField: boolean;
  allowSharpCorners: boolean;
  showGlobalNameField: boolean;
  showGlobalClassNameField: boolean;
  allowQuickSettings: boolean;
  showGlobalSettings: boolean;
  allowAddSticker: boolean;
}

interface GeneralSettings {
  appTitle?: string;
  appSubtitle?: string;
  appLogo?: string;
  adminReturnUrl?: string;
  makeWebhookUrl?: string;
  errorWebhookUrl?: string;
  driveFolderId?: string;
  showBackgroundsToPublic?: boolean;
  allowUserUploads?: boolean;
  iconCategories?: IconCategory[];
  bgCategories?: BackgroundCategory[];
  nameLabel?: string;
  classNameLabel?: string;
  iconLabel?: string;
  addStickerButtonLabel?: string;
  sharedDesignLabel?: string;
  selectIconButtonLabel?: string;
  defaultNameValue?: string;
  defaultClassNameValue?: string;
  uploadButtonLabel?: string;
  wizardKicker?: string; // Quick settings: small heading above the title
  wizardTitle?: string; // Quick settings: step 1 title
  wizardIntro?: string; // Quick settings: step 1 explanatory text
  faviconUrl?: string;
  permissions?: UserPermissions;
  aiGeneratorDailyLimit?: number; // Max AI icon generations per IP per day (server-enforced); logged-in admins are exempt
  aiGeneratorGlobalDailyLimit?: number; // Max AI icon images across all requests per day (server-enforced); applies to everyone, including admins
  defaultVariantSettings?: Record<string, VariantSettings>; // Stores admin-defined defaults
  sheetWidth?: number; // Print sheet size in mm (defaults to A4: 210x297)
  sheetHeight?: number;
  bleedEnabled?: boolean; // Whether the print file exports with bleed ("spadávka")
  bleedMm?: number; // Bleed size in mm, tenths precision (e.g. 1.1, 2.3)
  cutLinesEnabled?: boolean; // Whether the print file exports with vector cut paths ("ořezové cesty")
}

interface AssetSettings {
  customIcons?: IconType[];
  customBackgrounds?: CustomBackground[];
  customShapes?: CustomShape[];
  customTemplates?: CustomTemplate[];
}

const EMPTY_ASSETS: AssetSettings = { customIcons: [], customBackgrounds: [], customShapes: [], customTemplates: [] };

const sanitizeData = (data: any): any => {
    if (Array.isArray(data)) {
        return data.map(v => sanitizeData(v));
    } else if (data !== null && typeof data === 'object') {
        const newObj: any = {};
        Object.keys(data).forEach(key => {
            const val = data[key];
            if (val !== undefined) {
                newObj[key] = sanitizeData(val);
            }
        });
        return newObj;
    }
    return data;
};

export const settingsService = {
  // `failed` is set when the settings could not be read at all (offline, blocked reCAPTCHA/App Check): the caller
  // must not then carry on with the built-in defaults as if they were the shop's settings (wrong return URL!).
  loadGlobalData: async (): Promise<{ general: GeneralSettings; assets: AssetSettings; failed?: boolean }> => {
    if (!db) {
        console.warn("Firebase DB not initialized.");
        return { general: {}, assets: EMPTY_ASSETS, failed: true };
    }
    try {
        const instanceId = getInstanceId();
        const generalSnap = await getDoc(doc(db, SETTINGS_COLLECTION, instanceId));
        const [iconsSnap, bgsSnap, shapesSnap, templatesSnap] = await Promise.all([
            getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_ICONS)),
            getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_BGS)),
            getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_SHAPES)),
            getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_TEMPLATES))
        ]);

        const assets: AssetSettings = {
            customIcons: iconsSnap.docs.map(d => d.data() as IconType),
            customBackgrounds: bgsSnap.docs.map(d => d.data() as CustomBackground),
            customShapes: shapesSnap.docs.map(d => d.data() as CustomShape),
            customTemplates: templatesSnap.docs.map(d => d.data() as CustomTemplate)
        };

        return {
            general: generalSnap.exists() ? (generalSnap.data() as GeneralSettings) : {},
            assets: assets
        };
    } catch (e: any) {
        console.error("[Settings] Error loading data:", e);
        return { general: {}, assets: EMPTY_ASSETS, failed: true };
    }
  },

  saveGeneralSettings: async (settings: Partial<GeneralSettings>) => {
    if (!db) return;
    try {
        const cleanData = sanitizeData(settings);
        await setDoc(doc(db, SETTINGS_COLLECTION, getInstanceId()), cleanData, { merge: true });
    } catch (e) {
        console.error("Failed to save settings:", e);
        throw e;
    }
  },

  removeVariantDefault: async (variantId: string) => {
    if (!db) return;
    try {
        await setDoc(doc(db, SETTINGS_COLLECTION, getInstanceId()), {
            defaultVariantSettings: { [variantId]: deleteField() }
        }, { merge: true });
    } catch (e) {
        console.error("Failed to remove variant default:", e);
        throw e;
    }
  },

  saveCustomIcons: async (icons: IconType[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        const iconsToStore = icons.filter(i => i.isCustom || i.categoryId);
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_ICONS));
        const currentIds = new Set(iconsToStore.map(i => i.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        iconsToStore.forEach(icon => {
            const cleanIcon = sanitizeData({
                id: icon.id,
                name: icon.name,
                dataType: icon.dataType,
                data: icon.data,
                isCustom: !!icon.isCustom,
                categoryId: icon.categoryId || 'basic'
            });
            const iconRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_ICONS, icon.id);
            batch.set(iconRef, cleanIcon);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save icons to collection:", e);
        throw e;
    }
  },

  saveCustomBackgrounds: async (bgs: CustomBackground[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_BGS));
        const currentIds = new Set(bgs.map(b => b.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        bgs.forEach(bg => {
            const cleanBg = sanitizeData(bg);
            const bgRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_BGS, bg.id);
            batch.set(bgRef, cleanBg);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save backgrounds to collection:", e);
        throw e;
    }
  },

  saveCustomTemplates: async (templates: CustomTemplate[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_TEMPLATES));
        const currentIds = new Set(templates.map(t => t.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        templates.forEach(t => {
            const cleanTemplate = sanitizeData(t);
            const templateRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_TEMPLATES, t.id);
            batch.set(templateRef, cleanTemplate);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save templates to collection:", e);
        throw e;
    }
  },

  saveCustomShapes: async (shapes: CustomShape[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_SHAPES));
        const currentIds = new Set(shapes.map(s => s.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        shapes.forEach(shape => {
            const cleanShape = sanitizeData(shape);
            const shapeRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_SHAPES, shape.id);
            batch.set(shapeRef, cleanShape);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save shapes to collection:", e);
        throw e;
    }
  }
};
